import React, {useCallback, useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogHeader, DialogBody, DialogFooter} from '@stubhub/bamboo';

import CookieContent from './CookieContent/CookieContent';
import CookieFooter from './CookieFooter/CookieFooter';
import CookieHeader from './CookieHeader/CookieHeader';
import {BTN_TYPES} from './utils/btnTypes';
import {COOKIE_TYPE} from './utils/cookieType';

import '../styles/index.scss';

const CookieModalCore = ({onSubmit, className = ''}) => {
  const [showConfigOptions, setShowConfigOptions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeCookie, setActiveCookie] = useState({
    [COOKIE_TYPE.ESSENTIAL]: true,
    [COOKIE_TYPE.PERFORMANCE]: false,
    [COOKIE_TYPE.MARKETING]: false,
  });
  const headerRef = useRef(null);

  const handleShowConfigOptions = () => setShowConfigOptions(true);
  const openDialog = useCallback(() => {
    setIsOpen(true);
    setShowConfigOptions(false);
  }, []);
  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);
  const handleActiveCookie = (name, checked) =>
    setActiveCookie((activeCookie) => ({
      ...activeCookie,
      [name]: checked,
    }));

  const handleSubmit = (type) => {
    let cookieValues = activeCookie;
    if (BTN_TYPES.CUSTOM_COOKIES !== type) {
      const isAcceptAll = BTN_TYPES.ACCEPT_ALL === type;
      cookieValues = {
        [COOKIE_TYPE.ESSENTIAL]: true,
        [COOKIE_TYPE.PERFORMANCE]: isAcceptAll,
        [COOKIE_TYPE.MARKETING]: isAcceptAll,
      };
      setActiveCookie(cookieValues);
    }

    setIsOpen(false);
    onSubmit && onSubmit(cookieValues);
  };

  const showCookieModal = useCallback(
    (defaultTracking = false, defaultAdvertising = false) => {
      openDialog();

      setActiveCookie({
        [COOKIE_TYPE.ESSENTIAL]: true,
        [COOKIE_TYPE.PERFORMANCE]: defaultTracking,
        [COOKIE_TYPE.MARKETING]: defaultAdvertising,
      });

      return true;
    },
    [openDialog]
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.cookieConsent = {showCookieModal};
    }
  }, [showCookieModal]);

  return (
    <Dialog
      id="cookieModal__dialog"
      isOpen={isOpen}
      onDismiss={closeDialog}
      size="lg"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      className={`cookieModal__dialog ${className}`}
      portalClassName="cookieModal__portal"
      overlayClassName="cookieModal__overlay"
      initialFocusRef={headerRef}
    >
      <DialogHeader ref={headerRef} tabIndex={-1}>
        <CookieHeader showConfigOptions={showConfigOptions} />
      </DialogHeader>
      <DialogBody>
        <CookieContent
          showConfigOptions={showConfigOptions}
          handleActiveCookie={handleActiveCookie}
          activeCookie={activeCookie}
        />
      </DialogBody>
      <DialogFooter>
        <CookieFooter
          showConfigOptions={showConfigOptions}
          handleShowConfigOptions={handleShowConfigOptions}
          handleSubmit={handleSubmit}
        />
      </DialogFooter>
    </Dialog>
  );
};

CookieModalCore.displayName = 'CookieModalCore';

CookieModalCore.propTypes = {
  /**
   * Function executed when submit cookie modal
   */
  onSubmit: PropTypes.func,

  /**
   * ClassName assigned from speedy apps
   */
  className: PropTypes.string,
};

export default CookieModalCore;
